// Session management utilities

// Get user ID from Firebase auth if available
function getCurrentUserId(): string | null {
  try {
    // Check if Firebase auth state is available in localStorage
    const firebaseAuthKey = Object.keys(localStorage).find(k => k.startsWith('firebase:authUser:'));
    if (firebaseAuthKey) {
      const authData = JSON.parse(localStorage.getItem(firebaseAuthKey) || '{}');
      return authData.uid || null;
    }
    return null;
  } catch (_) {
    return null;
  }
}

/**
 * Get the session ID for the current user.
 * - For authenticated users: uses their Firebase UID as the session base
 * - For anonymous users: generates a random session ID stored in localStorage
 * 
 * This ensures:
 * - Logged-in users see their own data across devices/sessions
 * - Anonymous users have isolated sessions
 * - Logging out clears access to authenticated user data
 */
export function getSessionId(): string {
  try {
    const userId = getCurrentUserId();
    
    if (userId) {
      // Authenticated user: use their UID-based session
      // This ensures their data persists across browser sessions
      return `user_${userId}`;
    }
    
    // Anonymous user: use localStorage-based session
    let sid = localStorage.getItem('pinak_anon_session_id');
    if (!sid) {
      sid = 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('pinak_anon_session_id', sid);
    }
    return sid;
  } catch (_) {
    // Fallback for private browsing or disabled storage
    return 'temp_' + Math.random().toString(36).slice(2);
  }
}

/**
 * Clear the anonymous session (useful when user logs in/out)
 */
export function clearAnonymousSession(): void {
  try {
    localStorage.removeItem('pinak_anon_session_id');
  } catch (_) {
    // Ignore errors in private browsing mode
  }
}

/**
 * Check if the current session belongs to an authenticated user
 */
export function isAuthenticatedSession(): boolean {
  return getCurrentUserId() !== null;
}

